/**
 * @typedef {{
 *   params: { spaceBetween?: number },
 *   isLocked?: boolean,
 *   update: () => void,
 *   destroy: (deleteInstance?: boolean, cleanStyles?: boolean) => void
 * }} CompareSwiper
 */

/**
 * @typedef {HTMLElement & { compareSwiper?: CompareSwiper }} CompareContent
 */

/**
 * @typedef {Window & {
 *   Swiper?: new (element: Element, options: Record<string, unknown>) => CompareSwiper
 * }} CompareWindow
 */

const COMPARE_MOBILE_MQ = '(max-width: 749px)';
/** M 端产品列超过该数量时改为横向滚动，标题列固定在左侧 */
const COMPARE_MOBILE_SCROLL_MAX = 3;

/**
 * @returns {boolean}
 */
const isCompareMobile = () => window.matchMedia(COMPARE_MOBILE_MQ).matches;

/**
 * 产品信息列数量（不含左侧标题列）
 * @param {HTMLElement} wrapper
 * @returns {number}
 */
const getCompareProductCount = (wrapper) => wrapper.querySelectorAll('.compare-item-info').length;

/**
 * M 端且产品列超过 3：横向滑动产品，标题不滚动
 * @param {HTMLElement} wrapper
 * @returns {boolean}
 */
const isMobileScrollLayout = (wrapper) => isCompareMobile() && getCompareProductCount(wrapper) > COMPARE_MOBILE_SCROLL_MAX;

/**
 * @param {HTMLElement} wrapper
 */
const clearMobileStackedPlacement = (wrapper) => {
  const rows = /** @type {HTMLElement[]} */ ([...wrapper.querySelectorAll('.product-compare-item-row')]);

  rows.forEach((row) => {
    row.style.gridRow = '';
    row.style.gridColumn = '';
    row.style.width = '';
    row.style.minWidth = '';
    row.style.maxWidth = '';
    row.style.position = '';
    row.style.left = '';
    row.style.zIndex = '';

    if (row.closest('.compare-item-title') && row.getAttribute('data-row') === '1') {
      row.style.display = '';
    }
  });

  wrapper.style.gridTemplateColumns = '';
  wrapper.style.overflowX = '';
};

/**
 * Match live M-end: attribute label as full-width bar, then 3 product values underneath.
 * Uses CSS grid + display:contents; places each data-row onto explicit grid lines.
 * 产品列超过 3 个时仍用同一套堆叠尺寸，每列宽度按一屏 3 个计算，多出的列横向滑走，标题条固定。
 *
 * @param {HTMLElement} wrapper
 */
const applyMobileStackedLayout = (wrapper) => {
  const section = wrapper.closest('.product-comparison-table-section');

  if (!(section instanceof HTMLElement)) return;

  const mobile = isCompareMobile();
  const infoItems = /** @type {HTMLElement[]} */ ([...wrapper.querySelectorAll('.compare-item-info')]);
  const productCount = Math.max(infoItems.length, 1);
  const mobileScroll = mobile && infoItems.length > COMPARE_MOBILE_SCROLL_MAX;

  section.classList.toggle('product-comparison-table-section--mobile-stacked', mobile);
  section.classList.toggle('product-comparison-table-section--mobile-scroll', mobileScroll);
  section.style.setProperty('--compare-mobile-product-count', String(productCount));
  clearMobileStackedPlacement(wrapper);

  if (!mobile) {
    section.style.removeProperty('--compare-mobile-viewport');
    section.style.removeProperty('--compare-mobile-col-width');
    return;
  }

  // 列宽按当前可视区域的 1/3 写死，避免 1fr 把 8 列重新挤回一屏。
  const viewport = wrapper.clientWidth;
  const colWidth = viewport / COMPARE_MOBILE_SCROLL_MAX;

  section.style.setProperty('--compare-mobile-viewport', `${viewport}px`);
  section.style.setProperty('--compare-mobile-col-width', `${colWidth}px`);

  if (mobileScroll) {
    wrapper.style.gridTemplateColumns = `repeat(${productCount}, ${colWidth}px)`;
    wrapper.style.overflowX = 'auto';
  }

  const titleRows = /** @type {HTMLElement[]} */ ([
    ...wrapper.querySelectorAll('.compare-item-title .product-compare-item-row'),
  ]);

  let gridRow = 1;

  titleRows.forEach((titleRow) => {
    const rowIndex = titleRow.getAttribute('data-row');
    const infoRows = /** @type {HTMLElement[]} */ ([
      ...wrapper.querySelectorAll(`.compare-item-info .product-compare-item-row[data-row="${rowIndex}"]`),
    ]);

    if (rowIndex === '1') {
      titleRow.style.display = 'none';
      infoRows.forEach((row, index) => {
        row.style.gridRow = String(gridRow);
        row.style.gridColumn = String(index + 1);
        if (mobileScroll) {
          row.style.width = `${colWidth}px`;
          row.style.minWidth = `${colWidth}px`;
          row.style.maxWidth = `${colWidth}px`;
        }
      });
      gridRow += 1;
      return;
    }

    titleRow.style.display = '';
    titleRow.style.gridRow = String(gridRow);
    titleRow.style.gridColumn = '1 / -1';
    if (mobileScroll) {
      titleRow.style.position = 'sticky';
      titleRow.style.left = '0';
      titleRow.style.zIndex = '3';
      titleRow.style.width = `${viewport}px`;
      titleRow.style.minWidth = `${viewport}px`;
      titleRow.style.maxWidth = `${viewport}px`;
    }
    gridRow += 1;

    infoRows.forEach((row, index) => {
      row.style.gridRow = String(gridRow);
      row.style.gridColumn = String(index + 1);
      if (mobileScroll) {
        row.style.width = `${colWidth}px`;
        row.style.minWidth = `${colWidth}px`;
        row.style.maxWidth = `${colWidth}px`;
      }
    });
    gridRow += 1;
  });
};

/**
 * @param {HTMLElement} wrapper
 */
const syncCompareRowHeights = (wrapper) => {
  const mobile = isCompareMobile();
  const rows = /** @type {HTMLElement[]} */ ([...wrapper.querySelectorAll('.product-compare-item-row')]);
  const rowGroups = /** @type {Map<string, HTMLElement[]>} */ (new Map());

  rows.forEach((row) => {
    const rowIndex = row.getAttribute('data-row');
    row.style.height = '';

    if (!rowIndex) return;

    // 堆叠布局里标题单独成行，只同步产品单元格高度。
    if (mobile && row.closest('.compare-item-title')) return;

    if (!rowGroups.has(rowIndex)) {
      rowGroups.set(rowIndex, []);
    }

    rowGroups.get(rowIndex)?.push(row);
  });

  requestAnimationFrame(() => {
    // 同一 data-row 的所有单元格取最高高度，保证每列对应行水平对齐。
    rowGroups.forEach((groupRows) => {
      const maxHeight = Math.max(...groupRows.map((row) => row.getBoundingClientRect().height));

      groupRows.forEach((row) => {
        row.style.height = `${Math.ceil(maxHeight)}px`;
      });
    });

    updateMobileCompareScrollbar(wrapper);
  });
};

/**
 * @param {HTMLElement} wrapper
 */
const getCompareColumnGap = (wrapper) => {
  const gap = window.getComputedStyle(wrapper).getPropertyValue('--compare-column-gap');

  return Number.parseFloat(gap) || 0;
};

/**
 * 把滚动条轨道上的指针位置换算成内容 scrollLeft
 * @param {HTMLElement} wrapper
 * @param {HTMLElement} scrollbar
 * @param {number} clientX
 */
const scrollCompareFromTrack = (wrapper, scrollbar, clientX) => {
  const maxScroll = Math.max(wrapper.scrollWidth - wrapper.clientWidth, 0);

  if (maxScroll <= 0) return;

  const trackWidth = scrollbar.clientWidth;
  const thumbWidth = Math.max((wrapper.clientWidth / wrapper.scrollWidth) * trackWidth, 24);
  const maxThumb = Math.max(trackWidth - thumbWidth, 0);
  const rect = scrollbar.getBoundingClientRect();
  const thumbLeft = Math.min(Math.max(clientX - rect.left - thumbWidth / 2, 0), maxThumb);
  const ratio = maxThumb > 0 ? thumbLeft / maxThumb : 0;

  wrapper.scrollLeft = ratio * maxScroll;
};

/**
 * M 端自定义滚动条可拖拽（Swiper 在 M 端已销毁，需要自己接管）
 * @param {HTMLElement} wrapper
 */
const bindMobileScrollbarDrag = (wrapper) => {
  const section = wrapper.closest('.product-comparison-table-section');
  const scrollbar = section?.querySelector('.compare-swiper-scrollbar');

  if (!(scrollbar instanceof HTMLElement)) return;
  if (scrollbar.getAttribute('data-compare-drag-bound') === 'true') return;

  scrollbar.setAttribute('data-compare-drag-bound', 'true');

  let dragging = false;

  const stopDrag = () => {
    dragging = false;
    scrollbar.classList.remove('is-dragging');
  };

  scrollbar.addEventListener('pointerdown', (event) => {
    if (!isMobileScrollLayout(wrapper)) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    dragging = true;
    scrollbar.classList.add('is-dragging');
    scrollbar.setPointerCapture(event.pointerId);
    scrollCompareFromTrack(wrapper, scrollbar, event.clientX);
    event.preventDefault();
  });

  scrollbar.addEventListener('pointermove', (event) => {
    if (!dragging) return;

    scrollCompareFromTrack(wrapper, scrollbar, event.clientX);
    event.preventDefault();
  });

  scrollbar.addEventListener('pointerup', stopDrag);
  scrollbar.addEventListener('pointercancel', stopDrag);
};

/**
* 内容是否横向溢出（需要滚动）
* @param {HTMLElement} scroller
* @returns {boolean}
*/
const canHorizontallyScroll = (scroller) => scroller.scrollWidth - scroller.clientWidth > 1;

/**
* 同步底部自定义滚动条显隐与滑块（M 端原生横向滚动）
 * @param {HTMLElement} wrapper
 */
const updateMobileCompareScrollbar = (wrapper) => {
  const section = wrapper.closest('.product-comparison-table-section');
  const scrollbar = section?.querySelector('.compare-swiper-scrollbar');

  if (!(section instanceof HTMLElement) || !(scrollbar instanceof HTMLElement)) return;

 // 仅在 M 端横向滑动布局下显示；再按内容是否溢出决定显隐
  const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
  const canScroll = isMobileScrollLayout(wrapper) && maxScroll > 1;

  scrollbar.classList.toggle('compare-swiper-scrollbar--hidden', !canScroll);

  if (!canScroll) return;

  let drag = scrollbar.querySelector('.swiper-scrollbar-drag');

  if (!(drag instanceof HTMLElement)) {
    drag = document.createElement('div');
    drag.className = 'swiper-scrollbar-drag';
    scrollbar.appendChild(drag);
  }

  const trackWidth = scrollbar.clientWidth;
  const thumbWidth = Math.max((wrapper.clientWidth / wrapper.scrollWidth) * trackWidth, 24);
  const maxThumb = Math.max(trackWidth - thumbWidth, 0);
  const left = maxScroll > 0 ? (wrapper.scrollLeft / maxScroll) * maxThumb : 0;

  drag.style.width = `${thumbWidth}px`;
  drag.style.transform = `translate3d(${left}px, 0, 0)`;
};

/**
* 布局稳定后再量一次（图片/行高同步后避免误判）
* @param {HTMLElement} wrapper
*/
const scheduleCompareScrollbarUpdate = (wrapper) => {
 window.requestAnimationFrame(() => {
   window.requestAnimationFrame(() => {
     updateMobileCompareScrollbar(wrapper);
     syncDesktopCompareScrollbar(wrapper);
   });
 });
};

/**
* Desktop Swiper：按是否 lock / 是否溢出同步进度条显隐
* @param {HTMLElement} wrapper
*/
const syncDesktopCompareScrollbar = (wrapper) => {
 if (isCompareMobile()) return;

 const section = wrapper.closest('.product-comparison-table-section');
 const scrollbar = section?.querySelector('.compare-swiper-scrollbar');
 const compareContent = /** @type {CompareContent | null} */ (wrapper.querySelector('.compare-content'));

 if (!(section instanceof HTMLElement) || !(scrollbar instanceof HTMLElement) || !compareContent) return;

 const desktopEven = section.dataset.desktopEvenLayout === 'true';
 if (desktopEven) {
   scrollbar.classList.add('compare-swiper-scrollbar--hidden');
   return;
 }

 const swiper = compareContent.compareSwiper;
 if (!swiper) {
   scrollbar.classList.add('compare-swiper-scrollbar--hidden');
   return;
 }

 const locked = swiper.isLocked === true;
 const overflowing = canHorizontallyScroll(compareContent);
 scrollbar.classList.toggle('compare-swiper-scrollbar--hidden', locked || !overflowing);
};

/**
 * @param {HTMLElement} wrapper
 */
const bindMobileCompareScroll = (wrapper) => {
  if (wrapper.getAttribute('data-compare-scroll-bound') !== 'true') {
    wrapper.addEventListener('scroll', () => updateMobileCompareScrollbar(wrapper), { passive: true });
    wrapper.setAttribute('data-compare-scroll-bound', 'true');
  }

 if (wrapper.getAttribute('data-compare-resize-bound') !== 'true') {
   if (typeof ResizeObserver !== 'undefined') {
     const ro = new ResizeObserver(() => scheduleCompareScrollbarUpdate(wrapper));
     ro.observe(wrapper);
   }
   wrapper.setAttribute('data-compare-resize-bound', 'true');
 }

 scheduleCompareScrollbarUpdate(wrapper);
 bindMobileScrollbarDrag(wrapper);
};

/**
 * @param {HTMLElement} wrapper
 */
const initCompareSwiper = (wrapper) => {
  const section = wrapper.closest('.product-comparison-table-section');
  const compareContent = /** @type {CompareContent | null} */ (wrapper.querySelector('.compare-content'));
  const scrollbar = section?.querySelector('.compare-swiper-scrollbar');
  const themeWindow = /** @type {CompareWindow} */ (window);
  const desktopEven = section instanceof HTMLElement && section.dataset.desktopEvenLayout === 'true';
  const isDesktop = window.matchMedia('(min-width: 750px)').matches;
  const mobile = isCompareMobile();

  if (!compareContent || !scrollbar) return;

  // 桌面均分布局不滑动；M 端用堆叠 + 原生横向滚动，不走 Swiper。
  if ((desktopEven && isDesktop) || mobile) {
    compareContent.compareSwiper?.destroy(true, true);
    compareContent.compareSwiper = undefined;

    if (mobile) {
      bindMobileCompareScroll(wrapper);
    } else {
      scrollbar.classList.add('compare-swiper-scrollbar--hidden');
    }
    return;
  }

  if (typeof themeWindow.Swiper === 'undefined') {
    window.setTimeout(() => initCompareSwiper(wrapper), 100);
    return;
  }

  if (compareContent.compareSwiper) {
    compareContent.compareSwiper.params.spaceBetween = getCompareColumnGap(wrapper);
    compareContent.compareSwiper.update();
    scheduleCompareScrollbarUpdate(wrapper);
    return;
  }

  compareContent.compareSwiper = new themeWindow.Swiper(compareContent, {
    wrapperClass: 'compare-content-wrapper',
    slideClass: 'compare-item-info',
    slidesPerView: 'auto',
    spaceBetween: getCompareColumnGap(wrapper),
    watchOverflow: true,
    observer: true,
    observeParents: true,
    scrollbar: {
      el: scrollbar,
      draggable: true,
      hide: false,
    },
   on: {
     lock: () => syncDesktopCompareScrollbar(wrapper),
     unlock: () => syncDesktopCompareScrollbar(wrapper),
     resize: () => syncDesktopCompareScrollbar(wrapper),
     update: () => syncDesktopCompareScrollbar(wrapper),
    },
  });

 scheduleCompareScrollbarUpdate(wrapper);
};

/**
 * @param {ParentNode} root
 * @returns {string}
 */
const getCurrentProductHandle = (root) => {
  const fromDom =
    root instanceof Element
      ? root.closest('[data-current-product-handle]')?.getAttribute('data-current-product-handle')
      : '';
  if (fromDom) return fromDom.trim();

  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : '';
};

/**
 * @param {HTMLElement} item
 * @returns {string}
 */
const getColumnProductHandle = (item) => {
  const attr = item.getAttribute('data-product-handle');
  if (attr) return attr.trim();

  const link = item.querySelector('a[href*="/products/"]');
  if (!link) return '';

  const href = link.getAttribute('href') || '';
  const match = href.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : '';
};

/**
 * Keep the title column first; move the current product to the first info column.
 * Must run before Swiper wraps `.compare-item-info`.
 *
 * @param {HTMLElement} wrapper
 */
const bringCurrentProductColumnFirst = (wrapper) => {
  const currentHandle = getCurrentProductHandle(wrapper);
  if (!currentHandle) return;

  const infoItems = /** @type {HTMLElement[]} */ ([...wrapper.querySelectorAll(':scope > .compare-item-info')]);
  const currentItem = infoItems.find((item) => getColumnProductHandle(item) === currentHandle);

  if (!currentItem || infoItems[0] === currentItem) return;

  infoItems[0].before(currentItem);
};

/**
 * @param {HTMLElement} wrapper
 */
const initCompareTable = (wrapper) => {
  if (wrapper.getAttribute('data-compare-ready') !== 'true') {
    bringCurrentProductColumnFirst(wrapper);

    const infoItems = /** @type {HTMLElement[]} */ ([...wrapper.querySelectorAll(':scope > .compare-item-info')]);

    infoItems.forEach((item, index) => {
      item.setAttribute('data-index', String(index + 2));
    });

    if (infoItems.length > 0) {
      const compareContent = document.createElement('div');
      const compareContentWrapper = document.createElement('div');

      compareContent.className = 'compare-content swiper';
      compareContentWrapper.className = 'compare-content-wrapper swiper-wrapper';

      // 将除第一列外的产品信息列统一放入 Swiper 容器中。
      infoItems.forEach((item) => compareContentWrapper.appendChild(item));
      compareContent.appendChild(compareContentWrapper);
      wrapper.appendChild(compareContent);
    }

    const productItems = /** @type {HTMLElement[]} */ ([...wrapper.querySelectorAll('.product-compare-item')]);

    productItems.forEach((item) => {
      // 行外层由 _product-compare-item-row.liquid 输出，这里只补充行序号数据。
      const rows = /** @type {HTMLElement[]} */ ([...item.querySelectorAll(':scope > .product-compare-item-row')]);

      rows.forEach((row, index) => {
        row.setAttribute('data-row', String(index + 1));
      });
    });

    wrapper.setAttribute('data-compare-ready', 'true');
  }

  applyMobileStackedLayout(wrapper);
  syncCompareRowHeights(wrapper);
  initCompareSwiper(wrapper);
  scheduleCompareScrollbarUpdate(wrapper);

  // 图片加载完成后高度/宽度可能变化，需要重新同步行高与进度条显隐。
  const images = /** @type {HTMLImageElement[]} */ ([...wrapper.querySelectorAll('img')]);

  images.forEach((image) => {
    if (image.complete) return;
    image.addEventListener(
      'load',
      () => {
        applyMobileStackedLayout(wrapper);
        syncCompareRowHeights(wrapper);
        initCompareSwiper(wrapper);
        scheduleCompareScrollbarUpdate(wrapper);
      },
      { once: true }
    );
  });
};

const initCompareTables = () => {
  const wrappers = /** @type {HTMLElement[]} */ ([
    ...document.querySelectorAll('.product-comparison-table-section .compare-wrapper'),
  ]);

  wrappers.forEach(initCompareTable);
};

/** @type {ReturnType<typeof window.setTimeout> | undefined} */
let compareResizeTimer;

document.addEventListener('DOMContentLoaded', initCompareTables);
document.addEventListener('shopify:section:load', initCompareTables);

window.addEventListener('resize', () => {
  window.clearTimeout(compareResizeTimer);
  compareResizeTimer = window.setTimeout(() => {
    const wrappers = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll('.product-comparison-table-section .compare-wrapper'),
    ]);

    wrappers.forEach((wrapper) => {
      applyMobileStackedLayout(wrapper);
      initCompareSwiper(wrapper);
      syncCompareRowHeights(wrapper);
      scheduleCompareScrollbarUpdate(wrapper);
    });
  }, 120);
});
